import { useForm } from 'react-hook-form';
import { InputStateWrapper as HookedInput } from '../../admin/form-components/InputStateWrapper.tsx';
import { UpdateUserDto } from '../../gql/graphql.ts';
import { Button } from '@progress/kendo-react-buttons';
import { useState } from 'react';
import { PageTitle } from '../PageTitle.tsx';
import { Fieldset } from '../form-components/Fieldset.tsx';
import { DropdownStateWrapper } from '../form-components/DropdownStateWrapper.tsx';
import { EditorStateWrapper } from '../form-components/EditorStateWrapper.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import { BackButton } from '../../shared/BackButton.tsx';
import { Label } from '@progress/kendo-react-labels';
import { permissionTable } from '../../public/permissions.ts';
import { restApiUrl } from '../../shared/helpers/backendUrl';

// `password` is no longer part of UpdateUserDto (admins cannot set passwords on
// existing accounts). It is still collected here for the new-user flow, where
// the form feeds CreateUserDto and an initial password is allowed; the edit
// flow leaves `showPassword` off so it is never rendered or sent.
export type UserDto = Omit<
  UpdateUserDto,
  'allowOneDayReminder' | 'allowSevenDaysReminder'
> & { password?: string };

export interface UserFormSubmitData {
  formData: UserDto;
  file?: File | null;
  fileShouldBeDeleted?: boolean;
}

interface LecturerData {
  id: string;
  jobTitle?: string | null;
  vita?: string | null;
  qualification?: string | null;
  pathToProfileImage?: string | null;
  allowSevenDaysReminderForLecturer?: boolean | null;
  allowOneDayReminderForLecturer?: boolean | null;
}

interface UserFormProps {
  value?: UserDto;
  lecturer?: LecturerData;
  onSubmit: (data: UserFormSubmitData) => void;
  showPassword?: boolean;
}

export const UserForm: React.FC<UserFormProps> = ({
  value,
  lecturer,
  onSubmit,
  showPassword,
}) => {
  const sortedValuePrivileges = value?.privileges
    ? [...value.privileges].sort()
    : undefined;

  const currentRole = Object.values(permissionTable.rolePrivileges).find(
    (role) => {
      const sortedRolePrivileges = [...role.privileges].sort();
      return (
        JSON.stringify(sortedRolePrivileges) ===
        JSON.stringify(sortedValuePrivileges)
      );
    },
  );

  const formValues = {
    ...value,
    privileges: currentRole?.privileges ?? value?.privileges ?? [],
    ...(lecturer
      ? {
          lecturer: {
            jobTitle: lecturer.jobTitle ?? '',
            vita: lecturer.vita ?? '',
            qualification: lecturer.qualification ?? '',
          },
        }
      : {}),
  };

  const { control, formState, handleSubmit } = useForm<UserDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: formValues,
  });

  const [file, setFile] = useState<File | null>(null);
  const [fileShouldBeDeleted, setFileShouldBeDeleted] = useState(false);

  const profileImagePath = lecturer?.pathToProfileImage
    ? restApiUrl(lecturer.pathToProfileImage)
    : null;

  const onFormSubmit = (values: UserDto) => {
    onSubmit({ formData: values, file, fileShouldBeDeleted });
  };

  const { isValid, isDirty } = formState;

  return (
    <>
      <PageTitle title="Benutzer bearbeiten">
        <BackButton href="/admin/users" />
      </PageTitle>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend="Profil"
          description="Bearbeiten Sie die Profil-Informationen zu einem Benutzer."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <DropdownStateWrapper
              options={[
                { text: 'Herr', value: 'MR' },
                { text: 'Frau', value: 'MS' },
              ]}
              name={'address.title'}
              label={'Anrede'}
              rules={{ required: true }}
              control={control}
            />
            <HookedInput
              name={'address.firstName'}
              label={'Vorname'}
              placeholder="Lisa"
              control={control}
            />
            <HookedInput
              name={'address.lastName'}
              label={'Nachname'}
              placeholder="Musterfrau"
              control={control}
            />
            <HookedInput
              name={'address.email'}
              label={'E-Mail'}
              placeholder="test@example.com"
              control={control}
            />
            {showPassword && (
              <HookedInput
                name={'password'}
                label={'Passwort'}
                rules={{ required: true }}
                control={control}
              />
            )}
          </div>
        </Fieldset>
        <Fieldset
          legend="Berechtigungen"
          description="Über welche Berechtigungen verfügt der Benutzer?"
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <DropdownStateWrapper
              label="Rolle"
              options={Object.values(permissionTable.rolePrivileges).map(
                (role) => ({
                  text: role.displayName,
                  value: role.privileges,
                }),
              )}
              name={'privileges'}
              control={control}
              required={true}
              rules={{ required: true }}
            />
          </div>
        </Fieldset>

        {lecturer && (
          <Fieldset
            legend="Dozentenprofil"
            description="Bearbeiten Sie das Dozentenprofil dieses Benutzers."
          >
            <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
              <div className="flex flex-row gap-4">
                <div className="flex flex-col">
                  <Label
                    editorId="profiledisplay"
                    className="k-form-label mb-1"
                  >
                    Profilbild
                  </Label>
                  <div className="flex w-full flex-1 flex-col items-center justify-center gap-2 rounded-md border border-gray-200 p-4">
                    <div className="relative h-24 w-24 overflow-hidden rounded-full">
                      <button
                        onClick={() => {
                          if (file) {
                            // Clear the newly selected file and ensure we are not marking the existing image for deletion
                            setFile(null);
                            setFileShouldBeDeleted(false);
                          } else {
                            // Toggle deletion of the existing profile image to allow undo
                            setFileShouldBeDeleted((prev) => !prev);
                          }
                        }}
                        type="button"
                        className="group absolute inset-0 flex items-center justify-center hover:cursor-pointer hover:bg-black/30"
                        title="Bild löschen"
                      >
                        <HeroIcon
                          name={'Trash'}
                          className="hidden h-8 w-8 text-white/80 group-hover:block"
                        />
                      </button>
                      {file ? (
                        <img
                          className="h-full w-full object-cover"
                          src={URL.createObjectURL(file)}
                          alt="Profilbild"
                        />
                      ) : profileImagePath && !fileShouldBeDeleted ? (
                        <img
                          className="h-full w-full object-cover"
                          src={profileImagePath}
                          alt="Profilbild"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-300">
                          <HeroIcon
                            name={'UserCircle'}
                            variant="solid"
                            className="h-16 w-16 text-white/80"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 flex-col">
                  <Label editorId="profileimage" className="k-form-label mb-1">
                    Neues Bild hochladen
                  </Label>
                  <input
                    type={'file'}
                    id="profileimage"
                    name="profileimage"
                    accept={'image/png, image/jpeg'}
                    className="k-input-solid block h-9 w-full cursor-pointer rounded-md border border-gray-200 text-sm file:h-full file:rounded-l-md file:border-none file:bg-gray-200 file:px-2 file:hover:bg-gray-300"
                    onChange={(e) => {
                      e.preventDefault();
                      const selectedFile = e.currentTarget?.files?.[0];
                      if (selectedFile) {
                        setFile(selectedFile);
                        // A new file replaces the existing image, so clear any pending delete flag
                        setFileShouldBeDeleted(false);
                      }
                    }}
                  />
                </div>
              </div>

              <HookedInput
                name={'lecturer.jobTitle'}
                label={'Berufsbezeichnung'}
                control={control}
              />
              <HookedInput
                name={'lecturer.qualification'}
                label={'Qualifikation'}
                control={control}
              />
              <EditorStateWrapper
                name={'lecturer.vita'}
                label={'Vita'}
                control={control}
              />
            </div>
          </Fieldset>
        )}

        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || (!isDirty && !file && !fileShouldBeDeleted)}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
